import { ActionIcon, Box, Button, Flex, Image, Modal, SimpleGrid } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconChevronLeft, IconChevronRight, IconDownload } from "@tabler/icons-react";
import dayjs from "dayjs";
import { saveAs } from "file-saver";
import { useState } from "react";
import toast from "react-hot-toast";

export default function OrderImagesViewer({
  receiptImage,
  orderImages,
  label = "인수증",
  compact = false,
  fullWidth = false,
}) {
  const [opened, { open, close }] = useDisclosure();
  const [selectedIndex, setSelectedIndex] = useState(0);

  const allImages = [];

  if (orderImages?.data?.length > 0) {
    for (const img of orderImages.data) {
      allImages.push(img.attributes.url);
    }
  }

  if (receiptImage?.data?.attributes?.url) {
    allImages.push(receiptImage.data.attributes.url);
  }

  const hasImages = allImages.length > 0;
  const currentUrl = allImages[selectedIndex];

  async function handleDownload(url) {
    const targetUrl = url || currentUrl;
    if (!targetUrl) return;
    const response = await fetch(targetUrl, { mode: "cors", cache: "no-cache" });
    const blob = await response.blob();
    saveAs(blob, `인수증_${dayjs().format("YYYY_MM_DD_HH_mm_ss")}.jpeg`);
    toast.success("다운로드 완료");
  }

  function handlePrev() {
    setSelectedIndex((prev) => (prev > 0 ? prev - 1 : allImages.length - 1));
  }

  function handleNext() {
    setSelectedIndex((prev) => (prev < allImages.length - 1 ? prev + 1 : 0));
  }

  function handleOpen() {
    setSelectedIndex(0);
    open();
  }

  return (
    <>
      <Flex justify="center">
        <Button onClick={handleOpen} disabled={!hasImages} compact={compact} fullWidth={fullWidth}>
          {label}
          {allImages.length > 1 && `(${allImages.length})`}
        </Button>
      </Flex>

      <Modal
        opened={opened}
        onClose={close}
        title={`${label} 보기 ${allImages.length > 1 ? `(${selectedIndex + 1}/${allImages.length})` : ""}`}
        size="lg"
        zIndex={10000}
      >
        {allImages.length > 1 ? (
          <>
            <Flex align="center" gap="xs">
              <ActionIcon variant="light" size="xl" onClick={handlePrev}>
                <IconChevronLeft size={24} />
              </ActionIcon>
              <Image
                src={currentUrl}
                alt={`${label} ${selectedIndex + 1}`}
                withPlaceholder
                fit="contain"
                height={400}
                sx={{ flex: 1 }}
              />
              <ActionIcon variant="light" size="xl" onClick={handleNext}>
                <IconChevronRight size={24} />
              </ActionIcon>
            </Flex>

            <SimpleGrid cols={Math.min(allImages.length, 5)} mt="md" spacing="xs">
              {allImages.map((url, index) => (
                <Box key={url} pos="relative">
                  <Image
                    src={url}
                    alt={`썸네일 ${index + 1}`}
                    height={60}
                    fit="cover"
                    radius="sm"
                    onClick={() => setSelectedIndex(index)}
                    sx={(theme) => ({
                      cursor: "pointer",
                      border:
                        index === selectedIndex
                          ? `2px solid ${theme.colors.blue[5]}`
                          : "2px solid transparent",
                      opacity: index === selectedIndex ? 1 : 0.6,
                      "&:hover": { opacity: 1 },
                    })}
                  />
                  <ActionIcon
                    variant="filled"
                    color="blue"
                    size="sm"
                    pos="absolute"
                    bottom={6}
                    right={6}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownload(url);
                    }}
                  >
                    <IconDownload size={12} />
                  </ActionIcon>
                </Box>
              ))}
            </SimpleGrid>
          </>
        ) : (
          <Image src={currentUrl} alt={label} withPlaceholder />
        )}

        <Flex justify="flex-end" mt="md" gap="xs">
          <Button variant="default" onClick={close}>
            닫기
          </Button>
        </Flex>
      </Modal>
    </>
  );
}
